/*
 * UK ZIP wizard input field
 * <p>
 *
 * @author faraz.durrani
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2023 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component ( {
    selector: 'reporter-uk-zip',
    templateUrl: './uk-zip.component.html',
    styleUrls: ['./uk-zip.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UkZipComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => UkZipComponent),
            multi: true
        },
    ],
    standalone: false
} )

export class UkZipComponent implements ControlValueAccessor, Validator, AfterViewInit {

  @Input () placeholder: string;

  constructor( private renderer: Renderer2, private elementRef: ElementRef ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return `${ this.innerValue }`
      .toUpperCase ()
      .replace ( /\W|[_]/g, '' );
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;
  initialValue: any;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit() {
    this.initialValue = this.value;
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;
  }

  writeValue( value: any ): void {
    const newVal = value ? value.toUpperCase() : '';
    this.innerValue = newVal;
    this.renderer.setProperty ( this.elementRef.nativeElement, 'value', newVal );
    this.renderer.setProperty ( this.field.nativeElement, 'value', newVal );

    this.onChange ( newVal );
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( !control.value && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    if ( control.value && control.value !== this.initialValue && !/^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/.test ( control
        .value ) ) {
      return {
        patternMismatch: true,
        message: ' must be a valid UK ZIP.'
      };
    }
    return null;
  }
}
